import React from "react";
import { motion } from "framer-motion";
import { Check, Leaf, ShieldCheck, Package, Award, Lock, Scale, Droplets, Flame } from "lucide-react";

const ICONS = [ShieldCheck, Flame, Scale, Award, Package, Lock, Leaf, Droplets];

export const TrustStrip = ({ badges }) => {
  const items = [...badges, ...badges];
  return (
    <section aria-label="Product trust badges" className="bg-[#FBF4E1] border-y border-[#8D6E63]/15 overflow-hidden" data-testid="landing-trust-strip">
      <div className="py-5 sm:py-6">
        <div className="marquee-slow flex gap-10 sm:gap-16 whitespace-nowrap">
          {items.map((b, i) => (
            <div key={i} className="flex items-center gap-4 shrink-0">
              <span className="font-display italic text-lg sm:text-2xl text-[#143C18]">{b}</span>
              <span className="text-[#D4A017] text-base">✦</span>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {badges.map((b, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <motion.div
              key={b}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/70 border border-[#8D6E63]/12"
              data-testid={`trust-badge-${i}`}
            >
              <Icon className="w-4 h-4 text-[#2E7D32] shrink-0" strokeWidth={1.8} />
              <span className="text-xs sm:text-[13px] font-semibold text-[#143C18] leading-tight">{b}</span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export const Manifesto = ({ data, sideImage, sideAlt }) => (
  <section id="why" className="py-14 sm:py-20 lg:py-28 bg-[#FFF8E7] relative" data-testid="landing-manifesto">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <div className="chip mb-4"><Leaf className="w-3.5 h-3.5" /> {data.kicker}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18] leading-[1.08]">
            {data.heading[0]} <span className="block italic font-serif-alt text-[#2E7D32]">{data.heading[1]}</span>
          </h2>
          <p className="mt-5 text-[#5D4037] leading-relaxed max-w-md">{data.intro}</p>
          {sideImage && (
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="mt-8 rounded-[28px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(20,60,24,0.45)] aspect-square hidden lg:block">
              <img src={sideImage} alt={sideAlt} width="1254" height="1254" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </motion.div>
          )}
        </div>
      </div>
      <ol className="lg:col-span-7 divide-y divide-[#8D6E63]/15 border-t border-[#8D6E63]/15">
        {data.chapters.map((c, i) => (
          <motion.li
            key={c.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            className="group grid grid-cols-[3.5rem_1fr] sm:grid-cols-[5rem_1fr] gap-4 py-7 sm:py-9"
            data-testid={`manifesto-chapter-${i}`}
          >
            <span className="font-display text-2xl sm:text-3xl text-[#D4A017] leading-none pt-1 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-[#143C18] group-hover:text-[#2E7D32] transition-colors">{c.title}</h3>
              <p className="mt-2 text-[#5D4037] leading-relaxed text-[15px] sm:text-base max-w-xl">{c.desc}</p>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

export const PromiseSection = ({ data }) => {
  const icons = [Leaf, Flame, Package, ShieldCheck];
  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-cream-radial border-y border-[#8D6E63]/10" data-testid="landing-promise">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="chip chip-gold mb-4"><Award className="w-3.5 h-3.5" /> Why Choose Rudrima?</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18] leading-tight">{data.heading}</h2>
          <p className="mt-4 text-[#5D4037]">{data.sub}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.items.map((it, i) => {
            const Icon = icons[i % icons.length];
            return (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="p-7 rounded-3xl bg-white/80 border border-[#8D6E63]/12 hover:border-[#2E7D32]/40 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#143C18] flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#E8BE4A]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#143C18]">{it.title}</h3>
                <p className="mt-2 text-[#5D4037] text-[15px] leading-relaxed">{it.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const CheckItem = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-[#1A1614]">
    <span className="w-5 h-5 rounded-full bg-[#2E7D32]/10 flex items-center justify-center shrink-0 mt-0.5">
      <Check className="w-3 h-3 text-[#2E7D32]" strokeWidth={3} />
    </span>
    <span>{children}</span>
  </li>
);
